import { fetchPutState } from '@iwoca/lapi-client/edge';
import {
  fetchPutApiIntroducerPortalIntroducerApplication,
  PutApiIntroducerPortalIntroducerApplicationRequestBody,
} from '@iwoca/lapi-client/introducer_portal';
import { useMutation } from '@tanstack/react-query';
import { TSignupFormDataDE } from 'components/Signup/NewSignupFormDE/NewSignupFormDE.types';
import { useIntroducerData } from 'contexts/IntroducerData/IntroducerData.context';
import {
  TGetStateResponseDataDE,
  TPutStateRequestBodyDE,
} from 'lendingApi/lapiOverrides.types';
import { removeAllFalselyValues, trimAllStrings } from 'utils/common/common';
import {
  useGetIntroducerApplication,
  useGetSignupState,
} from '../../../../lendingApi/lendingApiHooks';
import {
  denormaliseApplicationDE,
  normaliseApplication,
} from '../../helpers/applicationNormalisation';
import {
  denormaliseStateDE,
  normaliseStateDE,
} from '../../helpers/stateNormalisation';
import { useRouterApplicationId } from '../useRouterApplicationId';
import { useInitializeState } from './useSignupDataDE.helpers';

export function useSignupDataDE() {
  const { introducerId } = useIntroducerData();
  const applicationIdFromUrl = useRouterApplicationId();
  const { application, applicationStatus } = useGetIntroducerApplication();
  const stateKey = application?.customer?.state_key;
  const { state, stateStatus, refetchState } =
    useGetSignupState<TGetStateResponseDataDE>();
  const { initializeStateAndApplication } = useInitializeState();

  const { mutateAsync: mutateState } = useMutation({
    mutationFn: async ({ state }: { state: TPutStateRequestBodyDE }) => {
      await fetchPutState({ stateKey: stateKey as string, body: state as any }); // Using any because lapi client currently doesn't have the Germany schemas
    },
  });

  const { mutateAsync: mutateApplication } = useMutation({
    mutationFn: async ({
      application,
      applicationId,
    }: {
      application: PutApiIntroducerPortalIntroducerApplicationRequestBody;
      applicationId: string;
    }) => {
      await fetchPutApiIntroducerPortalIntroducerApplication({
        introducerId: introducerId as string,
        applicationId: applicationId as string,
        body: application,
      });
    },
  });

  const updateSignupData = async (
    data: TSignupFormDataDE,
    saveForLater: boolean = false,
  ): Promise<{ applicationId: string }> => {
    const formattedData = removeAllFalselyValues(trimAllStrings(data));
    const stateData = denormaliseStateDE(state, formattedData);
    const applicationData = denormaliseApplicationDE(formattedData);
    let applicationId;

    if (!applicationIdFromUrl) {
      applicationId = await initializeStateAndApplication(
        stateData,
        applicationData,
        saveForLater,
      );

      if (!applicationId) {
        throw new Error('Error updating signup data');
      }
    } else {
      applicationId = applicationIdFromUrl;

      await mutateState({
        state: {
          data: {
            ...stateData,
            ui: {
              tip_latest_signup_step: saveForLater ? 'new' : 'new/documents',
            },
          },
        },
      });
      await mutateApplication({
        application: { data: applicationData },
        applicationId,
      });

      await refetchState();
    }
    return { applicationId };
  };

  return {
    stateKey: stateKey as string | undefined,
    stateData: normaliseStateDE(state),
    applicationData: normaliseApplication(application),
    updateSignupData,
    existingApplicationDataLoaded:
      state &&
      stateStatus === 'success' &&
      application &&
      applicationStatus === 'success',
  };
}
